import React, { useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DescriptionIcon from '@material-ui/icons/Description';
import Skeleton from '@material-ui/lab/Skeleton/Skeleton';
import { colors } from '@skyslope/mache';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useHistory, useParams } from 'react-router-dom';
import { withLaunchDarkly, type LaunchDarklyFlags } from '../../common/launchDarkly';
import SkeletonTable from '../../common/loading/SkeletonTable';
import NavHeader from '../../common/navigation/NavHeader';
import Footer from '../../components/common/Footer';
import FooterLinks from '../../components/common/FooterLinks';
import Search from '../../components/common/Search';
import { HeaderContext } from '../../context/header-context';
import useBottomScrollListener from '../../hooks/useBottomScrollListener';
import selectPropertyEmpty from '../../images/selectPropertyEmpty.svg';
import { getUsersGroups } from '../../lib/api/account/accountApi';
import {
  createEmptyEnvelopeDTM,
  createEmptyEnvelopeWithNoProperty,
  updateEnvelopeEmail,
  updateEnvelopeProperty,
} from '../../lib/api/envelope/envelope-api';
import { preferencesUrl } from '../../lib/environment-config';
import { useShallowSelector, useDispatch } from '../../lib/reduxHooks';
import useDebounce from '../../lib/useDebounce';
import { getTimezoneCode } from '../../lib/utils';
import type { IRootState } from '../../store';
import { fetchProperties, resetEndOfPropertiesReached, resetProperties } from '../../store/dtm/properties/actions';
import Empty from '../components/common/EmptyState';
import { PropertyStatus } from '../components/common/PropertyStatus';
import Table from '../components/common/Table';
import LoadingTable from '../components/loading/LoadingTable';
import type { IFile } from '../types';

dayjs.extend(advancedFormat);

const useStyles = makeStyles((theme: Theme) => ({
  properties: {
    maxWidth: 1200,
    width: '100%',
    margin: `${theme.spacing(7)}px ${theme.spacing(2)}px 70px`,
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(4),
  },
  tableControls: {
    marginBottom: theme.spacing(3),
  },
  divider: {
    height: 1,
    width: '100%',
    marginTop: 40,
    marginBottom: 32,
    backgroundColor: colors.grey[400],
  },
  count: {
    display: 'flex',
    alignItems: 'center',
  },
  countIcon: {
    marginRight: theme.spacing(1),
    color: colors.grey[500],
  },
  date: {
    fontWeight: 'normal',
  },
  templateCount: {
    display: 'flex',
    alignItems: 'center',
  },
  templateCountIcon: {
    marginRight: theme.spacing(1),
    color: colors.grey[500],
  },
  foundSkeleton: {
    width: 200,
  },
}));

interface IState {
  totalCount: number;
  properties: IFile[];
  salesEnd: boolean;
  listingEnd: boolean;
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selector = (state: IRootState) => ({
  totalCount: state.dtm.properties.totalCount,
  properties: state.dtm.properties.properties,
  salesEnd: state.dtm.properties.salesEnd,
  listingEnd: state.dtm.properties.listingEnd,
});

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const Properties = ({ flags }: { flags: LaunchDarklyFlags }) => {
  const store: IState = useShallowSelector(selector);
  const { properties, totalCount } = store;
  const params = useParams<{ id?: string }>();
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const [files, setFiles] = useState<IFile[]>([]);
  const [search, setSearch] = useState('');
  const [selectedFile, setSelectedFile] = React.useState<IFile | null>(null);
  const hasInit = React.useRef(false);
  const debouncedSearch = useDebounce(search, 200);
  const headerContext = React.useContext(HeaderContext);
  const [showBottomLoader, setShowBottomLoader] = useState(false);
  const [alreadySearched, setAlreadySearched] = useState(false);
  const [sortModifiedAsc, setSortModifiedAsc] = useState(false);
  const [activeSearchType, setActiveSearchType] = useState('');
  const [fetching, setFetching] = useState(false);
  const hideNavItems = flags && flags['hide-build-ui-elements'];
  const showPreferencesMenuItem = flags && flags['add-automatic-time-stamp-signatures-to-user-preferences'];
  const existingEnvelopeId = params.id;
  const [loadingTable, setLoadingTable] = useState(false);

  const tableColumns = [
    {
      key: 'propertyStr',
      label: 'Address',
      boldText: true,
    },
    {
      key: 'modifiedOn',
      label: 'Last Modified',
      sortKey: 'LASTMODIFIED',
      cell: (file: IFile) => (
        <Typography classes={{ root: classes.date }} variant="body2">
          {dayjs(file.modifiedOn).format('MM/DD/YY hh:mm A').concat(' ', getTimezoneCode())}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      cell: (file: IFile) => <PropertyStatus file={file} />,
    },
  ];

  useBottomScrollListener(() => {
    if (!(store.salesEnd && store.listingEnd)) {
      setShowBottomLoader(true);
      setActiveSearchType('scroll');
      dispatch(
        fetchProperties({
          searchValue: search,
          sortAsc: sortModifiedAsc,
          searchType: 'scroll',
        })
      );
    }
  });

  useEffect(() => {
    if (showBottomLoader) {
      setShowBottomLoader(false);
    }
    hasInit.current = true;
    setFiles(properties);
    setLoadingTable(false);
    setActiveSearchType('');
    setFetching(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [properties]);

  React.useEffect(() => {
    setLoadingTable(true);
    setActiveSearchType('init');
    dispatch(fetchProperties({ searchValue: '', sortAsc: sortModifiedAsc, searchType: 'init' }));
    headerContext.setContent(
      <NavHeader
        labelName="Select property"
        breadcrumb={{ name: 'Envelope Management', onClick: () => history.push('/envelopes') }}
        progressBarPercent={20}
        hideNavItems={hideNavItems}
        preferencesUrl={preferencesUrl}
        showPreferences={showPreferencesMenuItem}
        getUsersGroups={getUsersGroups}
        flags={flags}
      />
    );
    return () => {
      dispatch(resetProperties());
      headerContext.setContent(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, []);

  React.useEffect(() => {
    if (activeSearchType !== 'search') return;
    dispatch(fetchProperties({ searchValue: search, sortAsc: sortModifiedAsc, searchType: 'search' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- This code was written prior to enforcing lint rules
  }, [debouncedSearch]);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const onClickNext = async () => {
    if (!selectedFile) return;
    dispatch(resetEndOfPropertiesReached());
    let envelopeId: string;
    if (existingEnvelopeId) {
      await updateEnvelopeProperty(existingEnvelopeId, selectedFile);
      envelopeId = existingEnvelopeId;
    } else {
      envelopeId = await createEmptyEnvelopeDTM(selectedFile);
    }
    await updateEnvelopeEmail(envelopeId, selectedFile.propertyStr);
    history.push(`/dtm/envelopes/${envelopeId}/documents`);
  };

  if (!hasInit.current) {
    return null;
  }

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleRowClick = (file: IFile) => {
    setSelectedFile(selectedFile?.id === file.id ? null : file);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleTableSort = (_sortKey: string) => {
    const modifiedAsc = !sortModifiedAsc;
    setSortModifiedAsc(modifiedAsc);
    setLoadingTable(true);
    setActiveSearchType('sort');
    setFetching(true);
    dispatch(resetEndOfPropertiesReached());
    dispatch(fetchProperties({ searchValue: search, sortAsc: modifiedAsc, searchType: 'sort' }));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const returnTable = () => {
    if (activeSearchType === 'sort' || activeSearchType === 'search') {
      return (
        <>
          <div data-spec="skeleton-loading-table">
            <div className={classes.divider} />
            <div className={classes.tableControls}>
              <Typography variant="overline" className={classes.templateCount} data-spec="template-count">
                <DescriptionIcon className={classes.templateCountIcon} />
                <span className={classes.foundSkeleton}>
                  <Skeleton animation="wave" />
                </span>
              </Typography>
            </div>
            <SkeletonTable columns={tableColumns} />
          </div>
        </>
      );
    }
    return (
      <>
        <Table
          onClickRow={handleRowClick}
          columns={tableColumns}
          rows={properties}
          idKey="id"
          showBottomLoader={showBottomLoader}
          onClickHeader={handleTableSort}
          currentSortDesc={!sortModifiedAsc}
          currentSortKey="LASTMODIFIED"
          rowIsSelected={(file: IFile) => file.id === selectedFile?.id}
        />
      </>
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleEmptyClick = async () => {
    let envelopeId = '';
    if (!existingEnvelopeId) {
      envelopeId = await createEmptyEnvelopeWithNoProperty();
    }
    history.push(`/envelopes/${existingEnvelopeId || envelopeId}/documents`);
  };

  const emptyStateText = {
    heading: 'No results found',
    subtitle: 'Try changing your search terms or filters to find what you’re looking for.',
  };

  const noPropertiesText = {
    heading: 'There are no properties linked to this account.',
    subtitle: 'Continue without a property or create a transaction or listing in SkySlope to continue.',
    button: 'Continue Without Property',
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSearch = (value: string) => {
    if (!alreadySearched && value !== '') {
      setAlreadySearched(true);
    }
    setSearch(value.toLocaleLowerCase());
    setActiveSearchType('search');
    dispatch(resetEndOfPropertiesReached());
    setFetching(true);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const emptyState = () => {
    const emptyStateContent = search ? emptyStateText : noPropertiesText;
    return (
      <Empty
        icon={selectPropertyEmpty}
        heading={emptyStateContent.heading}
        headAria={emptyStateContent.heading}
        subtitle={emptyStateContent.subtitle}
        subAria={emptyStateContent.subtitle}
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        buttonText={totalCount ? null : emptyStateContent.button}
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        buttAria={totalCount ? null : emptyStateContent.button}
        actionButton={() => handleEmptyClick()}
      />
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const getPropertyCountString = () => {
    const properties = totalCount === 1 ? 'PROPERTY' : 'PROPERTIES';
    const filtered = search ? '(FILTERED)' : '';

    return `${totalCount.toLocaleString()} ${properties} FOUND ${filtered}`;
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const counter = () => {
    if (fetching || loadingTable) return null;
    return (
      <>
        <div className={classes.divider} />
        <div className={classes.tableControls}>
          <Typography variant="overline" className={classes.count} data-spec="count">
            <DescriptionIcon className={classes.countIcon} />
            {getPropertyCountString()}
          </Typography>
        </div>
      </>
    );
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const contentToRender = () => {
    if (loadingTable) {
      return <LoadingTable />;
    }
    if (files.length < 1 && activeSearchType !== 'search') {
      return emptyState();
    }
    return returnTable();
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const headerContent = () => {
    if (!loadingTable) {
      return (
        <>
          <Typography variant="h3" className={classes.header}>
            Select a property
          </Typography>
          <Search value={search} onChange={(value) => handleSearch(value)} />
        </>
      );
    }
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const footerContent = () => {
    if (!loadingTable) {
      return (
        <>
          <Footer>
            {hideNavItems ? (
              ''
            ) : (
              <Button color="primary" onClick={() => history.push('/envelopes')}>
                Exit
              </Button>
            )}
            <Button
              color="primary"
              variant="contained"
              disabled={!selectedFile}
              onClick={onClickNext}
              data-spec="next-button"
            >
              Next
            </Button>
          </Footer>
          <FooterLinks />
        </>
      );
    }
  };

  return (
    <div className={classes.properties}>
      {headerContent()}
      {counter()}
      {contentToRender()}
      {footerContent()}
    </div>
  );
};

// eslint-disable-next-line import/no-default-export -- This code was written prior to enforcing lint rules
export default withLaunchDarkly(Properties);
